/**
 * bioluminescence — soft pulsing glow cells anchored in the scene (fungal caps,
 * abyss lure-organisms, deep-sea life). Each cell is a large soft additive sprite
 * parked at its world position that PULSES on a slow sine (alpha swell → recede)
 * on its own async cycle, evoking organic biolume — NOT the fast shimmer of
 * `flicker` (torch/heat) or the wobble of `caustics` (water-light). A drifting
 * ambient bed (Z_LAYERS.ATMOSPHERE, mooded:true).
 *
 * Read rule (hard-won): additive soft sprites need ≥~0.5 alpha + saturated colour
 * to read over busy/dark skies. Biolume over the near-black abyss/fungal sky
 * especially — keep `alphaMax` ≥ 0.5 and colours saturated (magenta/cyan/teal).
 *
 * Same builder shape as the kit: narrow by kind, place from the seed, mint soft
 * sprites behind IRenderer (null-safe), drive them with ONE shared ctx.addTick,
 * return a sprite handle, self-register. REDUCE-MOTION: the pulse is the builder's
 * own tick → when reduced we place the cells STATIC at their mid-alpha and never
 * tick. The pref is read ONCE at build.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { seededRand } from '../atmospherePlacement';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { lerp } from '../../../../shared/utils/MathUtils';
import type { ISprite } from '../../../rendering/IRenderer';

const TWO_PI = Math.PI * 2;

const buildBioluminescence: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'bioluminescence') return null;
  const { region } = ctx;
  const spanY = region.y1 - region.y0;
  const reduceMotion = getMotionPrefs().reduceMotion;

  const sprites: ISprite[] = [];
  // Per-cell pulse state: sprite, base x/y, async period + phase.
  const cells: { s: ISprite; baseX: number; baseY: number; periodMs: number; phase: number }[] = [];

  for (let i = 0; i < layer.count; i += 1) {
    if (!vfxBudgetManager.canSpawnShape()) break;
    const seed = ctx.seed + 9900 + i * 31.3;
    const size = lerp(layer.sizeMin, layer.sizeMax, seededRand(seed));
    const x = lerp(region.x0, region.x1, seededRand(seed + 1));
    const y = region.y0 + spanY * lerp(layer.bandYMin, layer.bandYMax, seededRand(seed + 2));
    // Mid-alpha when static (reduce-motion) so the cells still read.
    const alpha = lerp(layer.alphaMin, layer.alphaMax, 0.5);
    // glow_soft = the diffuse cell (a biolume cap/organ reads as a soft glow, not a sharp ripple).
    const s = createGlowSprite({ x, y, size, color: layer.color, alpha, textureKey: 'glow_soft', blendMode: 'add', parent: ctx.layerContainer });
    if (!s) break; // textures not ready — quieter scene, never a crash
    sprites.push(s);
    if (!reduceMotion) {
      cells.push({ s, baseX: x, baseY: y, periodMs: lerp(layer.periodMinMs, layer.periodMaxMs, seededRand(seed + 3)), phase: seededRand(seed + 4) });
    }
  }

  if (!sprites.length) return null;
  if (reduceMotion || !cells.length) return makeContentHandle({ sprites });

  let elapsedMs = 0;
  const unsub = ctx.addTick((dtMs: number) => {
    elapsedMs += dtMs;
    const v = ctx.getCameraView();
    if (v) {
      const cameraLeft = v.centerX - v.width / 2;
      const cameraRight = v.centerX + v.width / 2;
      const margin = 200;
      const span = v.width + margin * 2;
      for (const c of cells) {
        while (c.baseX < cameraLeft - margin) c.baseX += span;
        while (c.baseX > cameraRight + margin) c.baseX -= span;
      }
    }
    for (const c of cells) {
      const ph = (elapsedMs / c.periodMs + c.phase) % 1;
      // Slow sine pulse: alpha sweeps min→max→min over the period (organic swell + recede).
      const a = lerp(layer.alphaMin, layer.alphaMax, 0.5 - 0.5 * Math.cos(ph * TWO_PI));
      c.s.setAlpha(a);
      // Gentle organic drift — a small horizontal sway so the cells breathe in place.
      c.s.setPosition(c.baseX + Math.sin(ph * TWO_PI) * layer.driftPx, c.baseY);
    }
  });

  return makeContentHandle({ sprites, onDestroy: unsub });
};

registerEffect({ kind: 'bioluminescence', build: buildBioluminescence, depth: Z_LAYERS.ATMOSPHERE });
